import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';

import { apiCall } from '../api';


const Stories = () => {
    // The stories page - displays stories that a user can read

    const { author } = useParams(); // if an author is included in the url

    const [masterStories, setMasterStories] = useState([]); // stories from the backend
    const [stories, setStories] = useState([]); // the stories to display - can be filtered and sorted

    const [sortBy, setSortBy] = useState('popular'); // for how to sort the stories -- options: popular, title, author

    const [searchOpen, setSearchOpen] = useState(false); // if the search bar is open
    const searchInputRef = useRef(null); // Create a ref for the search input - to focus on it when the search bar is opened

    // load the stories from the backend
    useEffect(() => {

        const loadStories = async () => {

            // load the stories
            const [storiesSuccess, storiesResp] = await apiCall({
                method: 'get',
                url: 'perilandintrigue/api/stories/',
                params: { author: author },
            });

            // load the authors
            const [authorsSuccess, authorsResp] = await apiCall({
                method: 'get',
                url: 'perilandintrigue/api/authors/',
                data: {},
            });

            if (!storiesSuccess || !authorsSuccess) {
                alert('Failed to load stories');
                return;
            }

            // add the authors to the stories
            for (const story of storiesResp) {
                for (const author of authorsResp) {
                    if (story.author === author.id) {
                        story.author = author.username;
                        break;
                    }
                }
            }

            setMasterStories(storiesResp);

            // sort them by views by default
            const sortedStoriesResp = sortStories(storiesResp);
            // set the display stories
            setStories(sortedStoriesResp);
        };

        loadStories();
    }, []);

    // for focusing on the search input when the user clicks the search button
    useEffect(() => {

        if (searchOpen && searchInputRef.current) {
            searchInputRef.current.focus(); // Focus the input when searchOpen is true
        }
    }, [searchOpen]);

    // for sorting, when the sortBy value changes
    useEffect(() => {

        const sortedStories = sortStories(stories);
        setStories(sortedStories);
    }, [sortBy]);

    // toggle the search bar open or closed
    const toggleSearchOpen = () => setSearchOpen(!searchOpen);

    // sort the stories by the sortBy value
    const sortStories = (stories) => {

        const sortedStories = [...stories];

        sortedStories.sort((a, b) => {
            if (sortBy === 'popular') {
                // primary sort by views
                const viewsDiff = b.views - a.views;
                if (viewsDiff !== 0) {
                    return viewsDiff;
                }
                // secondary sort by title
                return a.title.localeCompare(b.title);
            } else if (sortBy === 'title') {
                return a.title.localeCompare(b.title);
            } else if (sortBy === 'author') {
                // primary sort by author
                const authorDiff = a.author.localeCompare(b.author);
                if (authorDiff !== 0) {
                    return authorDiff;
                }
                // secondary sort by title
                return a.title.localeCompare(b.title);
            }
        });

        return sortedStories;
    };

    // filter the stories by the filter value
    const filterStories = (stories, filter) => {
        const filteredStories = stories.filter((story) => {
            // get the title, author, and description, if they exist
            const title = story.title ? story.title : '';
            const author = story.author ? story.author : '';
            const description = story.description ? story.description : '';

            // check if the title, author, or description contains the filter value
            return (
                title.toLowerCase().includes(filter.toLowerCase()) ||
                author.toLowerCase().includes(filter.toLowerCase()) ||
                description.toLowerCase().includes(filter.toLowerCase())
            );
        });

        return filteredStories;
    };

    // handle the search input
    const handleSearch = (e) => {
        // get the filter value
        const filter = e.target.value;

        // filter the stories
        const filteredStories = filterStories(masterStories, filter);

        // sort the stories
        const sortedStories = sortStories(filteredStories);

        setStories(sortedStories);
    };

    // render the stories
    const renderStories = () => {

        return stories.map((story, index) => {
            
            // get the url name for the story
            const storyURLName = story.title.replace(/ /g, '-').toLowerCase();

            return (
                <div
                    key={index}
                    className='text'
                    style={{
                        width: '100%',
                        cursor: 'pointer',
                        userSelect: 'none',
                    }}
                    onClick={() => window.location.href = `/stories/${story.id}-${storyURLName}`}
                >
                    <div
                        className='container'
                        style={{
                            display: 'flex',
                            width: '100%',
                            flexDirection: 'row',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                        }}
                    >
                        <div className='stories-story-title'>{story.title}</div>
                        <div className='stories-story-author'>{story.author}</div>
                    </div>
                    <div className='stories-story-description'>{story.description}</div>
                    <div className='story-underline' />
                </div>
            );
        });
    };

    // render the sort by options
    const renderSortBy = () => {
        const sortByOptions = ['popular', 'title', 'author'];
        const transformXValues = {
            popular: '6%',
            title: '104%',
            author: '196%',
        };

        return (
            <div className="sort-by-container">
                {sortByOptions.map((option, index) => (
                    <div
                        key={index}
                        className={(sortBy === option) ? "sort-by-option text sort-by-option selected" : "sort-by-option text sort-by-option"}
                        onClick={() => setSortBy(option)}
                    >
                        {option}
                    </div>
                ))}
                <div
                    className='highlight-shape'
                    style={{ transform: `translateX(${transformXValues[sortBy]})` }}
                />
            </div>
        );
    };

    // render the search bar
    const renderSearch = () => {
        if (!searchOpen) {
            return (
                <div className='search-bar-container'>
                    <div
                    className='search-icon fas fa-search search-button'
                    onClick={() => toggleSearchOpen()}
                    />
                </div>
                
            );
        } else {
            return (
                <div className='search-bar-container open'>
                    <input
                        ref={searchInputRef}
                        className='search-bar search-text'
                        placeholder='Search'
                        onChange={(e) => handleSearch(e)}
                    />
                    <div
                    className='search-icon fas fa-search search-button'
                    onClick={() => toggleSearchOpen()}
                    />
                    
                </div>
                
            );
        }
    };

    return (
        <div className='container flex-column stories-container' style={{ height: '100%', alignItems: 'start' }}>
            <div
                className='container'
                style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                }}
            >
                <h1
                    className='text title'
                    style={{
                        marginBottom: '2%',
                        marginTop: '8%',
                    }}
                >
                    Stories
                </h1>
                {renderSortBy()}
            </div>

            <div
                className='container'
                style={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'end',
                    width: '100%',
                }}
            >
                {renderSearch()}
            </div>

            <div className='title-underline' style={{ marginBottom: '1%' }} />

            {renderStories()}

            <div style={{ minHeight: '100px' }} />
        </div>
    );
};

export default Stories;
